/**
 * Image validation — shared by every upload surface (listings, ad banners…).
 *
 * Validates the four things that matter for a safe, predictable upload:
 *   1. File type      → extension whitelist
 *   2. MIME type      → declared browser MIME whitelist
 *   3. File size      → per-preset byte ceiling
 *   4. Dimensions     → min / max, or exact (with tolerance) for banner slots
 *
 * The exact same rules are enforced again server-side (see backend/src/middleware/upload.ts)
 * so a client can never bypass validation by calling the API directly.
 */

export interface ImageRule {
  /** Stable id used for cache-busting / debugging. */
  key: string;
  /** Human label shown in the uploader + error copy. */
  label: string;
  /** Accepted MIME types (lower-case). */
  allowedMimeTypes: string[];
  /** Accepted extensions without the leading dot (lower-case). */
  allowedExtensions: string[];
  /** Maximum file size in bytes. */
  maxSizeBytes: number;
  /** Minimum accepted width in px. */
  minWidth?: number;
  /** Minimum accepted height in px. */
  minHeight?: number;
  /** Maximum accepted width in px. */
  maxWidth?: number;
  /** Maximum accepted height in px. */
  maxHeight?: number;
  /** Required width in px (banner slots). */
  exactWidth?: number;
  /** Required height in px (banner slots). */
  exactHeight?: number;
  /** Allowed +/- px deviation when exactWidth/exactHeight are set. */
  tolerancePx?: number;
}

export interface ImageDimensions {
  width: number;
  height: number;
}

export interface ImageValidationResult {
  ok: boolean;
  errors: string[];
  dimensions?: ImageDimensions;
}

const MB = 1024 * 1024;

export const IMAGE_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "webp"];

/** Cover photo shown in the directory + on the business profile hero. */
export const LISTING_COVER_RULE: ImageRule = {
  key: "listing-cover",
  label: "Cover image",
  allowedMimeTypes: IMAGE_MIME_TYPES,
  allowedExtensions: IMAGE_EXTENSIONS,
  maxSizeBytes: 5 * MB,
  minWidth: 600,
  minHeight: 400,
  maxWidth: 6000,
  maxHeight: 6000,
};

/** Square business logo / avatar mark. */
export const LISTING_LOGO_RULE: ImageRule = {
  key: "listing-logo",
  label: "Logo",
  allowedMimeTypes: IMAGE_MIME_TYPES,
  allowedExtensions: IMAGE_EXTENSIONS,
  maxSizeBytes: 2 * MB,
  minWidth: 128,
  minHeight: 128,
  maxWidth: 2000,
  maxHeight: 2000,
};

/** Additional photos in the listing gallery. */
export const LISTING_GALLERY_RULE: ImageRule = {
  key: "listing-gallery",
  label: "Gallery photo",
  allowedMimeTypes: IMAGE_MIME_TYPES,
  allowedExtensions: IMAGE_EXTENSIONS,
  maxSizeBytes: 5 * MB,
  minWidth: 500,
  minHeight: 350,
  maxWidth: 6000,
  maxHeight: 6000,
};

/** Advertisement banner — must match the placement slot dimensions. */
export function adBannerRule(width: number, height: number): ImageRule {
  const safeWidth = Math.max(1, Math.round(width));
  const safeHeight = Math.max(1, Math.round(height));
  return {
    key: `ad-banner-${safeWidth}x${safeHeight}`,
    label: `Banner (${safeWidth} × ${safeHeight} px)`,
    allowedMimeTypes: IMAGE_MIME_TYPES,
    allowedExtensions: IMAGE_EXTENSIONS,
    maxSizeBytes: 3 * MB,
    exactWidth: safeWidth,
    exactHeight: safeHeight,
    tolerancePx: Math.max(6, Math.round(Math.min(safeWidth, safeHeight) * 0.04)),
    maxWidth: 4000,
    maxHeight: 4000,
  };
}

export function formatBytes(bytes: number): string {
  if (bytes >= MB) return `${(bytes / MB).toFixed(bytes % MB === 0 ? 0 : 1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

/** Lower-case extension without the dot, or an empty string when absent. */
export function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf(".");
  if (dot < 0 || dot === fileName.length - 1) return "";
  return fileName.slice(dot + 1).toLowerCase();
}

/** Human-readable summary of a rule — used as the dropzone hint. */
export function describeRule(rule: ImageRule): string {
  const parts: string[] = [];
  parts.push(rule.allowedExtensions.map((e) => e.toUpperCase()).join(", "));
  parts.push(`up to ${formatBytes(rule.maxSizeBytes)}`);
  if (rule.exactWidth && rule.exactHeight) {
    parts.push(`${rule.exactWidth} × ${rule.exactHeight} px`);
  } else if (rule.minWidth && rule.minHeight) {
    parts.push(`${rule.minWidth} × ${rule.minHeight} px minimum`);
  }
  return parts.join(" · ");
}

/** Read real pixel dimensions from a file — prefers the fast bitmap path. */
export async function readImageDimensions(file: Blob): Promise<ImageDimensions> {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file);
      const dimensions = { width: bitmap.width, height: bitmap.height };
      bitmap.close?.();
      return dimensions;
    } catch {
      /* fall through to the <img> path */
    }
  }

  return new Promise<ImageDimensions>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("The image could not be read."));
    };
    image.src = url;
  });
}

/** Validate a single file against a rule. Never throws. */
export async function validateImageFile(
  file: File,
  rule: ImageRule,
): Promise<ImageValidationResult> {
  const errors: string[] = [];

  // 1 — extension (file type)
  const extension = extensionOf(file.name);
  if (!extension || !rule.allowedExtensions.includes(extension)) {
    errors.push(`Unsupported file type. Allowed: ${rule.allowedExtensions.map((e) => e.toUpperCase()).join(", ")}.`);
  }

  // 2 — MIME
  if (!rule.allowedMimeTypes.includes(file.type.toLowerCase())) {
    errors.push(`Unsupported file format. Allowed: ${rule.allowedExtensions.map((e) => e.toUpperCase()).join(", ")}.`);
  }

  // 3 — size
  if (file.size > rule.maxSizeBytes) {
    errors.push(`File is too large (${formatBytes(file.size)}). Maximum is ${formatBytes(rule.maxSizeBytes)}.`);
  } else if (file.size === 0) {
    errors.push("The selected file is empty.");
  }

  // Bail before the expensive dimension read when the basics are already wrong.
  if (errors.length > 0) return { ok: false, errors };

  // 4 — dimensions
  let dimensions: ImageDimensions;
  try {
    dimensions = await readImageDimensions(file);
  } catch {
    return { ok: false, errors: ["The image could not be read — please try another file."] };
  }

  if (dimensions.width === 0 || dimensions.height === 0) {
    return { ok: false, errors: ["The image has no readable dimensions."] };
  }

  if (rule.exactWidth && rule.exactHeight) {
    const tolerance = rule.tolerancePx ?? 0;
    const widthOff = Math.abs(dimensions.width - rule.exactWidth) > tolerance;
    const heightOff = Math.abs(dimensions.height - rule.exactHeight) > tolerance;
    if (widthOff || heightOff) {
      errors.push(
        `Image must be ${rule.exactWidth} × ${rule.exactHeight} px for this slot (yours is ${dimensions.width} × ${dimensions.height} px).`,
      );
    }
  } else {
    if (rule.minWidth && dimensions.width < rule.minWidth) {
      errors.push(`Image is too narrow (${dimensions.width} px). Minimum width is ${rule.minWidth} px.`);
    }
    if (rule.minHeight && dimensions.height < rule.minHeight) {
      errors.push(`Image is too short (${dimensions.height} px). Minimum height is ${rule.minHeight} px.`);
    }
  }

  if (rule.maxWidth && dimensions.width > rule.maxWidth) {
    errors.push(`Image is too wide (${dimensions.width} px). Maximum width is ${rule.maxWidth} px.`);
  }
  if (rule.maxHeight && dimensions.height > rule.maxHeight) {
    errors.push(`Image is too tall (${dimensions.height} px). Maximum height is ${rule.maxHeight} px.`);
  }

  return { ok: errors.length === 0, errors, dimensions };
}